import React, { useRef, useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * ContentEditable input that renders emotes as images while typing
 * Maintains cursor position and allows normal text editing
 */
const EmoteInput = ({ 
  value, 
  onChange, 
  onKeyPress,
  placeholder = "Type a message...",
  globalEmotes = [],
  customEmotes = [],
  disabled = false,
  className = ""
}) => {
  const editableRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Combine all emotes
  const allEmotes = [...globalEmotes, ...customEmotes];

  // Convert text with :emotename: to HTML with images
  const textToHTML = (text) => {
    if (!text) return '';
    if (allEmotes.length === 0) return text;
    
    let html = text;
    
    // Replace each emote code with an image or emoji
    allEmotes.forEach(emote => {
      const pattern = new RegExp(`:${emote.name}:`, 'g');
      
      if (emote.emoji) {
        // Global emoji-based emote
        html = html.replace(
          pattern,
          `<span class="inline-emote" contenteditable="false" data-emote="${emote.name}" style="font-size: 1.5em; vertical-align: middle; display: inline-block; user-select: none;">${emote.emoji}</span>`
        );
      } else {
        // Custom image-based emote
        html = html.replace(
          pattern,
          `<img src="${BACKEND_URL}/api${emote.file_path_28}" alt=":${emote.name}:" class="inline-emote" contenteditable="false" data-emote="${emote.name}" style="display: inline-block; width: 28px; height: 28px; vertical-align: middle; margin: 0 2px; user-select: none;" />`
        );
      }
    });
    
    return html;
  };

  // Convert HTML back to plain text with emote codes
  const htmlToText = (htmlElement) => {
    if (!htmlElement) return '';
    
    let text = '';
    const nodes = htmlElement.childNodes;
    
    for (let node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Check if it's an emote (img or span with data-emote)
        const emoteName = node.getAttribute('data-emote');
        if (emoteName) {
          text += `:${emoteName}:`;
        } else {
          text += node.textContent;
        }
      }
    }
    
    return text;
  };

  // Update display when value changes externally
  useEffect(() => {
    if (!editableRef.current) return;
    
    const currentText = htmlToText(editableRef.current);
    
    // If emotes are loaded and value contains emote codes, render them
    // Otherwise just show the plain text
    const html = (allEmotes.length > 0 && value.includes(':')) 
      ? textToHTML(value) 
      : value;
    
    // Update if value changed
    if (currentText !== value) {
      const selection = window.getSelection();
      const hadFocus = document.activeElement === editableRef.current;
      
      // Use textContent for plain text, innerHTML for rendered emotes
      if (html === value) {
        editableRef.current.textContent = value;
      } else {
        editableRef.current.innerHTML = html;
      }
      
      // Restore cursor to end if it was focused
      if (hadFocus && isFocused) {
        try {
          const range = document.createRange();
          range.selectNodeContents(editableRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          console.error('Failed to restore cursor:', e);
        }
      }
    }
  }, [value, isFocused, allEmotes]);

  // Handle input changes
  const handleInput = () => {
    if (!editableRef.current) return;
    
    // Save cursor position
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const cursorOffset = range ? range.startOffset : 0;
    const cursorNode = range ? range.startContainer : null;
    
    // Get plain text value
    const plainText = htmlToText(editableRef.current);
    
    // Check if we need to render new emotes
    const emotePattern = /:(\w+):/g;
    let match;
    let needsUpdate = false;
    
    while ((match = emotePattern.exec(plainText)) !== null) {
      const emoteName = match[1];
      const emote = allEmotes.find(e => e.name === emoteName);
      
      if (emote) {
        needsUpdate = true;
        break;
      }
    }
    
    // If we found emote codes that need rendering, update the HTML
    if (needsUpdate) {
      const newHTML = textToHTML(plainText);
      editableRef.current.innerHTML = newHTML;
      
      // Try to restore cursor position
      try {
        if (cursorNode && range) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (e) {
        // If cursor restoration fails, just place at end
        const newRange = document.createRange();
        newRange.selectNodeContents(editableRef.current);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
    
    // Call onChange with plain text value
    onChange(plainText);
  };

  // Handle paste - strip formatting and convert to plain text
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  return (
    <div
      ref={editableRef}
      contentEditable={!disabled}
      onInput={handleInput}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      className={`emote-input ${className}`}
      data-placeholder={placeholder}
      style={{
        minHeight: '40px',
        maxHeight: '120px',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        outline: 'none',
      }}
      suppressContentEditableWarning={true}
    />
  );
};

export default EmoteInput;
